import React, { useEffect, useMemo, useState, useRef } from "react";
import Card from "components/card";
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    flexRender,
} from "@tanstack/react-table";
import { IoChevronDown, IoChevronForward, IoCloseOutline } from "react-icons/io5";
import { MdDownload } from "react-icons/md";
import { TbCalculator } from "react-icons/tb";
import { Button } from '@aws-amplify/ui-react';
import TooltipHorizon from "components/tooltip";
import { useDealData } from "contexts/DealContext";
import { usePortfolioData } from "contexts/PortfolioContext";
import { useViewMode } from "contexts/ViewModeContext";
import { exportFinanceTableToCSV } from "utils/csvExport";
import {
    revenueOrder as canonicalRevenueOrder,
    expenseOrder as canonicalExpenseOrder,
    sortByMetricOrder,
} from "utils/metricOrder";

const columnHelper = createColumnHelper();

// Helper to check if a period is Projection 1 (uses calculated values)
export const isProjection1Period = (period) => {
    if (!period) return false;
    return /^Projection\s*1$/i.test(period);
};

// Helper to check if any period in array is Projection 1
export const hasProjection1Period = (periods) => {
    if (!periods || !Array.isArray(periods)) return false;
    return periods.some(period => isProjection1Period(period));
};

// Calculated Projections Indicator Badge
export const CalculatedProjectionsBadge = () => (
    <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-50 border border-amber-200 rounded-md">
        <TbCalculator className="w-4 h-4 text-amber-600" />
        <span className="text-xs font-medium text-amber-700">Calculated Projections</span>
    </div>
);


// Helper function to format cell values
export const formatCellValue = (value, isPPD = false) => {
    if (!value) return '-';
    if (isNaN(value)) return value;
    return isPPD ?
        `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` :
        `$${Math.round(Number(value)).toLocaleString()}`;
};

// Helper function to filter out EBITDA/EBITDAR items
export const filterEbitdaItems = (items) => {
    return items.filter(item =>
        item && item.Type &&
        !item.Type.toUpperCase().includes('EBITDA') &&
        !item.Type.toUpperCase().includes('EBITDAR') &&
        !item.Type.includes('Unmapped') &&
        !item.Type.includes("Total Payroll Taxes Benefits")
    );
};

// Time Period Selector Component
export function TimePeriodSelector({ availablePeriods = [], selectedPeriods, onTogglePeriod, onResetPeriods }) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const dropdownRef = useRef(null);

    // Check if we have any available periods
    const hasPeriods = Array.isArray(availablePeriods) && availablePeriods.length > 0;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Filter periods based on search
    const filteredPeriods = useMemo(() => {
        return searchQuery && hasPeriods
            ? availablePeriods.filter(period =>
                period.toLowerCase().includes(searchQuery.toLowerCase())
            )
            : availablePeriods;
    }, [availablePeriods, searchQuery, hasPeriods]);

    const isAllSelected = hasPeriods && availablePeriods.length === selectedPeriods.length;

    const handleSelectAll = (checked) => {
        if (checked) {
            onTogglePeriod(availablePeriods);
        } else {
            onTogglePeriod([]);
        }
    };

    const handleSearchFocus = (e) => {
        e.stopPropagation();
        if (!isOpen) setIsOpen(true);
    };

    // Show a count of available periods in the placeholder
    const placeholderText = hasPeriods
        ? "Select time periods"
        : "No time periods available";

    return (
        <div className="relative flex items-center gap-2 flex-wrap min-w-0" ref={dropdownRef}>
            <div className="flex items-center border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                <input
                    type="text"
                    placeholder={placeholderText}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={handleSearchFocus}
                    className="w-44 px-3 py-1.5 text-sm text-gray-700 bg-white rounded-l-md focus:outline-none"
                    disabled={!hasPeriods}
                />
                {searchQuery && (
                    <TooltipHorizon
                        content="Clear search"
                        placement="top"
                        extra="text-xs bg-opacity-50 text-gray-700"
                        trigger={
                            <button
                                onClick={() => setSearchQuery('')}
                                className="p-2 text-gray-500 hover:bg-gray-100 rounded-md transition-colors"
                            >
                                <IoCloseOutline className="w-5 h-5" />
                            </button>
                        }
                    />
                )}
                <button
                    onClick={() => hasPeriods && setIsOpen(!isOpen)}
                    className={`px-3 py-2 text-gray-400 hover:text-gray-500 focus:outline-none ${!hasPeriods ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                    <IoChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                <TooltipHorizon
                    content="Reset to current period only"
                    placement="top"
                    extra="text-xs bg-opacity-50 text-gray-700"
                    trigger={
                        <button
                            onClick={onResetPeriods}
                            className={`p-2 text-gray-500 hover:bg-gray-100 rounded-md transition-colors ${!hasPeriods ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={!hasPeriods}
                        >
                            <IoCloseOutline className="w-5 h-5" />
                        </button>
                    }
                />
            </div>

            {isOpen && hasPeriods && (
                <div className="absolute z-[500] top-full mt-1 left-0 w-64 bg-white border border-gray-200 rounded-md shadow-lg">
                    <div className="p-2 max-h-96 overflow-y-auto">
                        <label className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer border-b border-gray-200">
                            <input
                                type="checkbox"
                                checked={isAllSelected}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm font-semibold text-gray-700">Select All Periods</span>
                        </label>

                        {filteredPeriods.length === 0 && (
                            <div className="p-4 text-center text-gray-500">
                                No time periods match your search
                            </div>
                        )}

                        <div className="space-y-1 mt-2">
                            {filteredPeriods.map((period) => (
                                <label
                                    key={period}
                                    className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedPeriods.includes(period)}
                                        onChange={() => onTogglePeriod([period])}
                                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">{period}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {selectedPeriods.length > 0 && (
                <div className="flex flex-wrap gap-1.5 items-center">
                    {selectedPeriods.map(period => (
                        <span
                            key={period}
                            className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded"
                        >
                            {period}
                            <button
                                onClick={() => onTogglePeriod([period])}
                                className="ml-1 text-blue-600 hover:text-blue-800"
                            >
                                <IoCloseOutline className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

export function TableContainer({ children, title, timePeriodSelector, onExport, showCalculatedBadge }) {
    return (
        <Card extra={"w-full h-full p-4"}>
            <div className="mb-2 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                    {timePeriodSelector}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {showCalculatedBadge && <CalculatedProjectionsBadge />}
                    {onExport && (
                        <Button
                            onClick={onExport}
                            size="large"
                            className="flex items-center justify-center rounded-lg p-2 text-xl transition-opacity hover:cursor-pointer hover:opacity-80">
                            <MdDownload />
                        </Button>
                    )}
                </div>
            </div>
            {/* Table wrapper: fit to viewport, scrollable, sticky header/col preserved */}
            <div className="finance-table-scroll relative border border-gray-300 overflow-auto h-[calc(100vh-155px)]" style={{ scrollbarGutter: 'stable both-edges' }}>
                <table className="border-collapse w-full relative">
                    {children}
                </table>
            </div>
        </Card>
    );
}

export function TableHeader({ headerGroups, dark = false }) {
    return (
        <thead>
            {headerGroups.map((headerGroup, groupIndex) => (
                <tr
                    key={headerGroup.id}
                    className={`sticky border-b ${dark ? 'bg-gray-700 border-gray-600 [&_p]:!text-white' : 'bg-white border-gray-300'}`}
                    style={{ top: `${groupIndex * 28}px`, zIndex: 30 }}
                >
                    {headerGroup.headers.map((header, headerIndex) => {
                        // Determine if this is a period group header (parent header)
                        const isPeriodGroup = header.column.columnDef.isPeriodGroup;

                        // Check if this is the first column (Type)
                        const isTypeColumn = header.column.id === 'type';

                        // Check if this is the last column in the row
                        const isLastColumn = headerIndex === headerGroup.headers.length - 1;

                        // Add border to the right for period groups to separate them, but not for the last column
                        let borderClass = '';
                        if (!isLastColumn) {
                            if (isPeriodGroup) {
                                borderClass = dark ? 'border-r-2 border-gray-500' : 'border-r-2 border-gray-300';
                            } else if (!isTypeColumn) {
                                const columnId = header.column.id;
                                if (columnId.includes('-ppd') ||
                                    columnId.includes('-percentages') ||
                                    columnId.endsWith('-ppd-percentages')) {
                                    borderClass = dark ? 'border-r-2 border-gray-500' : 'border-r-2 border-gray-300';
                                }
                            }
                        }

                        // Check if this is the first row (which contains period headers)
                        // and the first column (which should be blank)
                        const isFirstRowTypeColumn = groupIndex === 0 && isTypeColumn;

                        // Enhanced styling for frozen column
                        const frozenColumnStyles = isTypeColumn ? {
                            position: 'sticky',
                            left: 0,
                            zIndex: 40,
                            backgroundColor: dark ? '#374151' : 'white',
                            boxShadow: dark ? '2px 0 4px rgba(0,0,0,0.3)' : '2px 0 4px rgba(0,0,0,0.1)'
                        } : {};

                        const isProjectionHeader = !isTypeColumn && /projection/i.test(header.column.id);
                        const projectionHeaderBg = isProjectionHeader
                            ? { backgroundColor: dark ? '#1e3a8a' : '#dbeafe' }
                            : {};

                        return (
                            <th
                                key={header.id}
                                colSpan={header.colSpan}
                                onClick={header.column.getToggleSortingHandler()}
                                className={`cursor-pointer min-w-[120px]
                                    ${dark ? 'border-r border-gray-600 py-1 px-2' : 'border-b border-gray-200 pb-2 pr-4 pt-2 dark:border-white/30'}
                                    ${isTypeColumn ? 'text-start min-w-[200px]' : 'text-center'}
                                    ${borderClass}`}
                                style={{ ...frozenColumnStyles, ...projectionHeaderBg }}
                            >
                                <div className={`items-center justify-between text-xs
                                    ${dark ? 'text-gray-300' : 'text-gray-200'}
                                    ${isTypeColumn ? '' : 'text-center'}
                                    ${isPeriodGroup ? `font-bold text-sm ${dark ? 'text-white' : 'text-gray-600'}` : ''}`}
                                >
                                    {/* Render nothing in the first row's Type column */}
                                    {isFirstRowTypeColumn ? (
                                        ""
                                    ) : (
                                        flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )
                                    )}
                                </div>
                            </th>
                        );
                    })}
                </tr>
            ))}
        </thead>
    );
}

// Helper function to determine if a cell is the last in a period group
export const isLastInPeriodGroup = (cell, row, isLastColumn) => {
    // If this is the last column in the table, don't add a border
    if (isLastColumn) {
        return false;
    }

    const cellId = cell.column.id;
    // Check if this is a subcolumn and the last one in its group
    if (cellId.includes('-ppd') ||
        cellId.includes('-percentages') ||
        cellId.endsWith('-ppd-percentages')) {
        return true;
    }
    return false;
};

function RevenueTable({ data, availableTimePeriods = [], currentPeriod }) {
    const { viewMode } = useViewMode();
    const { currentFacility: dealFacility, currentDealName } = useDealData();
    const { currentPortfolioFacility, currentPortfolioName } = usePortfolioData();
    const currentFacility = viewMode === 'portfolio' ? currentPortfolioFacility : dealFacility;
    const entityName = viewMode === 'portfolio' ? currentPortfolioName : currentDealName;
    const [tableData, setTableData] = useState([]);
    const [selectedPeriods, setSelectedPeriods] = useState(
        availableTimePeriods.length > 0 ? availableTimePeriods : [currentPeriod]
    );

    useEffect(() => {
        if (
            availableTimePeriods.length > 0 &&
            (selectedPeriods.length !== availableTimePeriods.length ||
                !selectedPeriods.every(p => availableTimePeriods.includes(p)))
        ) {
            setSelectedPeriods(availableTimePeriods);
        } else if (currentPeriod && selectedPeriods.length === 0) {
            setSelectedPeriods([currentPeriod]);
        }
    }, [currentPeriod, availableTimePeriods]);

    useEffect(() => {
        // First, filter out EBITDA items
        const filteredData = filterEbitdaItems(data);

        // Sort using the canonical revenue order
        const sortedData = sortByMetricOrder(filteredData, canonicalRevenueOrder);

        setTableData(sortedData);
    }, [data]);

    // Create columns based on selected time periods with grouping
    const columns = useMemo(() => {
        const typeColumn = columnHelper.accessor("Type", {
            id: "type",
            header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">TYPE</p>,
            cell: (info) => {
                const value = info.getValue();
                return (
                    <p className="text-sm font-bold text-gray-900">{value || '-'}</p>
                );
            },
        });

        // Order periods according to their order in availableTimePeriods
        const orderedPeriods = [...selectedPeriods].sort((a, b) => {
            const indexA = availableTimePeriods.indexOf(a);
            const indexB = availableTimePeriods.indexOf(b);
            return indexA - indexB;
        });

        // Create a column group for each selected period
        const periodColumnGroups = orderedPeriods.map(period => {
            // Create a group column for this period
            return columnHelper.group({
                id: `${period}-group`,
                header: period ? period.toUpperCase() : '',
                isPeriodGroup: true, // Custom property to identify period groups
                columns: [
                    // Annual column for this period
                    columnHelper.accessor(row => {
                        // Get the data for this period from the facility's periods
                        const periodData = currentFacility?.periods?.[period]?.Categories?.Revenue || [];
                        const matchingItem = periodData.find(item => item.Type === row.Type);
                        return matchingItem?.Annual;
                    }, {
                        id: `${period}-annual`,
                        header: () => <p className="text-sm font-bold text-right text-gray-600 dark:text-white">ANNUAL</p>,
                        cell: (info) => (
                            <p className="text-right text-sm text-gray-900">
                                {formatCellValue(info.getValue())}
                            </p>
                        ),
                    }),
                    // PPD column for this period
                    columnHelper.accessor(row => {
                        // Get the data for this period from the facility's periods
                        const periodData = currentFacility?.periods?.[period]?.Categories?.Revenue || [];
                        const matchingItem = periodData.find(item => item.Type === row.Type);
                        return matchingItem?.PPD;
                    }, {
                        id: `${period}-ppd`,
                        header: () => <p className="text-sm font-bold text-right text-gray-600 dark:text-white">PPD</p>,
                        cell: (info) => (
                            <p className="text-right text-sm text-red-900">
                                {formatCellValue(info.getValue(), true)}
                            </p>
                        ),
                    }),
                ],
            });
        });

        return [typeColumn, ...periodColumnGroups];
    }, [selectedPeriods, availableTimePeriods, currentFacility]);

    const [columnFilters, setColumnFilters] = useState([]);
    const [globalFilter, setGlobalFilter] = useState("");

    const table = useReactTable({
        data: tableData,
        columns,
        state: {
            columnFilters,
            globalFilter,
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const handleTogglePeriod = (periods) => {
        setSelectedPeriods(prev => {
            // If we're setting an entirely new array of periods
            if (periods.length > 1) {
                return periods;
            }

            const period = periods[0];
            // Toggle individual period
            if (prev.includes(period)) {
                // Don't allow removing the current period
                if (period === currentPeriod && prev.length === 1) {
                    return prev;
                }
                return prev.filter(p => p !== period);
            } else {
                return [...prev, period];
            }
        });
    };

    const handleResetPeriods = () => {
        setSelectedPeriods(
            availableTimePeriods.length > 0 ? availableTimePeriods : [currentPeriod]
        );
    };

    const handleExport = () => {
        const dealName = entityName || (viewMode === 'portfolio' ? 'portfolio' : 'deal');
        const facilityName = currentFacility?.details?.Facilities || 'facility';
        const periodsText = selectedPeriods.length > 1 ? 'multiple_periods' : selectedPeriods[0];
        const filename = `${dealName}_${facilityName}_revenue_${periodsText}.csv`;

        exportFinanceTableToCSV(
            tableData,
            columns,
            currentFacility,
            selectedPeriods,
            filename
        );
    };

    const timePeriodSelector = (
        <TimePeriodSelector
            availablePeriods={availableTimePeriods}
            selectedPeriods={selectedPeriods}
            onTogglePeriod={handleTogglePeriod}
            onResetPeriods={handleResetPeriods}
        />
    );

    return (
        <TableContainer
            title="Revenue"
            timePeriodSelector={timePeriodSelector}
            onExport={handleExport}
        >
            <TableHeader headerGroups={table.getHeaderGroups()} />
            <tbody>
                {table.getRowModel().rows.map((row, index) => (
                    <tr
                        key={row.id}
                        className={`
                            border-t
                            ${row.original.Type.toLowerCase().includes('adjusted')
                                ? 'bg-gray-100 font-semibold border-t-2 border-gray-400'
                                : row.original.Type.toLowerCase().includes('total')
                                    ? 'bg-gray-50 font-medium border-t border-gray-300'
                                    : index % 2 === 0 ? 'bg-white' : 'bg-blue-50'
                            }
                        `}
                    >
                        {row.getVisibleCells().map((cell, cellIndex) => {
                            // Check if this is the last cell in the row
                            const isLastColumn = cellIndex === row.getVisibleCells().length - 1;
                            const isTypeColumn = cell.column.id === 'type';

                            // Determine if this cell is the last in a period group
                            const isLastInGroup = isLastInPeriodGroup(cell, row, isLastColumn);
                            const borderClass = isLastInGroup ? 'border-r-2 border-gray-300' : '';

                            // Enhanced styling for frozen column
                            const frozenColumnStyles = isTypeColumn ? {
                                position: 'sticky',
                                left: 0,
                                zIndex: 20,
                                backgroundColor: 'inherit',
                                boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                            } : {};

                            return (
                                <td
                                    key={cell.id}
                                    className={`px-3 py-2 ${isTypeColumn
                                        ? 'text-left min-w-[200px]'
                                        : 'text-right min-w-[120px]'
                                        } ${borderClass}`}
                                    style={frozenColumnStyles}
                                >
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                    )}
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </TableContainer>
    );
}

function ExpenseTable({ data, availableTimePeriods = [], currentPeriod }) {
    const { viewMode } = useViewMode();
    const { currentFacility: dealFacility, currentDealName } = useDealData();
    const { currentPortfolioFacility, currentPortfolioName } = usePortfolioData();
    const currentFacility = viewMode === 'portfolio' ? currentPortfolioFacility : dealFacility;
    const entityName = viewMode === 'portfolio' ? currentPortfolioName : currentDealName;
    const [tableData, setTableData] = useState([]);
    const [expandedRows, setExpandedRows] = useState({});
    const [selectedPeriods, setSelectedPeriods] = useState(
        availableTimePeriods.length > 0 ? availableTimePeriods : [currentPeriod]
    );

    useEffect(() => {
        if (
            availableTimePeriods.length > 0 &&
            (selectedPeriods.length !== availableTimePeriods.length ||
                !selectedPeriods.every(p => availableTimePeriods.includes(p)))
        ) {
            setSelectedPeriods(availableTimePeriods);
        } else if (currentPeriod && selectedPeriods.length === 0) {
            setSelectedPeriods([currentPeriod]);
        }
    }, [currentPeriod, availableTimePeriods]);

    useEffect(() => {
        // First, filter out EBITDA items
        const filteredData = filterEbitdaItems(data);

        // Define category mappings
        const categoryMap = {
            'Housekeeping Laundry': {
                mainType: 'Housekeeping Laundry',
                subTypes: ['Housekeeping - Labor', 'Housekeeping - Non Labor']
            },
            'Food and Nutrition': {
                mainType: 'Food and Nutrition',
                subTypes: ['Food and Nutrition - Labor', 'Food and Nutrition - Non Labor']
            },
            'Maintenance': {
                mainType: 'Maintenance',
                subTypes: ['Maintenance - Labor', 'Maintenance - Non Labor']
            },
            'Recreation': {
                mainType: 'Recreation',
                subTypes: ['Recreation - Labor', 'Recreation - Non Labor']
            },
            'Payroll Taxes Benefits': {
                mainType: 'Payroll Taxes Benefits',
                subTypes: ['Payroll Taxes', 'Workers Comp', 'Benefits']
            }
        };

        // Process the data while maintaining order
        const processedData = [];
        const processedTypes = new Set();
        const categoryStorage = {};

        // Initialize category storage
        Object.entries(categoryMap).forEach(([category, config]) => {
            categoryStorage[category] = {
                subcategories: [],
                mainItem: null,
                hasBeenProcessed: false
            };
        });

        // Helper to calculate category totals
        const calculateCategoryTotals = (items, categoryName) => {
            // Special handling for Payroll Taxes Benefits
            if (categoryName === 'Payroll Taxes Benefits') {
                const totalPercentage = items.reduce((sum, item) => {
                    // Check if Percentages is defined
                    if (item.Percentages === undefined || item.Percentages === null) {
                        return sum;
                    }

                    // Handle both number and string types
                    let numValue;
                    if (typeof item.Percentages === 'number') {
                        numValue = item.Percentages;
                    } else if (typeof item.Percentages === 'string') {
                        numValue = parseFloat(item.Percentages.replace('%', ''));
                    } else {
                        numValue = 0;
                    }

                    return sum + (isNaN(numValue) ? 0 : numValue);
                }, 0);

                return {
                    Annual: items.reduce((sum, item) => sum + (Number(item.Annual) || 0), 0),
                    PPD: items.reduce((sum, item) => sum + (Number(item.PPD) || 0), 0),
                    Percentages: `${totalPercentage.toFixed(1)}%`
                };
            }

            // Default handling for other categories
            return {
                Annual: items.reduce((sum, item) => sum + (Number(item.Annual) || 0), 0),
                PPD: items.reduce((sum, item) => sum + (Number(item.PPD) || 0), 0)
            };
        };

        // Helper to find which category an item belongs to
        const findCategory = (type) => {
            return Object.entries(categoryMap).find(([_, config]) =>
                config.mainType === type || config.subTypes.includes(type)
            );
        };

        // First pass: collect all subcategories
        filteredData.forEach(item => {
            const categoryEntry = findCategory(item.Type);
            if (categoryEntry) {
                const [categoryName, config] = categoryEntry;
                if (config.subTypes.includes(item.Type)) {
                    categoryStorage[categoryName].subcategories.push(item);
                } else if (item.Type === config.mainType) {
                    categoryStorage[categoryName].mainItem = item;
                }
            }
        });

        // Second pass: process items in order
        filteredData.forEach(item => {
            if (processedTypes.has(item.Type)) return;

            const categoryEntry = findCategory(item.Type);

            if (categoryEntry) {
                const [categoryName, config] = categoryEntry;

                // If this is a main category and we haven't processed it yet
                if (item.Type === config.mainType && !categoryStorage[categoryName].hasBeenProcessed) {
                    const storage = categoryStorage[categoryName];
                    if (storage.subcategories.length > 0) {
                        const totals = calculateCategoryTotals(storage.subcategories, categoryName);
                        processedData.push({
                            ...storage.mainItem,
                            Type: categoryName,
                            Annual: totals.Annual,
                            PPD: totals.PPD,
                            Percentages: totals.Percentages,  // This will only be present for Payroll Taxes Benefits
                            isMainCategory: true,
                            subcategories: storage.subcategories
                        });
                    }
                }
                // Skip subcategories as they're handled with their main category
                processedTypes.add(item.Type);
            } else {
                // Not a category item, add directly
                processedData.push(item);
                processedTypes.add(item.Type);
            }
        });

        // Sort using the canonical expense order
        const sortedData = sortByMetricOrder(processedData, canonicalExpenseOrder);

        setTableData(sortedData);
    }, [data]);

    const toggleRowExpansion = (rowId) => {
        setExpandedRows(prev => ({
            ...prev,
            [rowId]: !prev[rowId]
        }));
    };

    // Create columns based on selected time periods with grouping
    const columns = useMemo(() => {
        const typeColumn = columnHelper.accessor("Type", {
            id: "type",
            header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">TYPE</p>,
            cell: (info) => {
                const row = info.row.original;
                const value = info.getValue();

                if (!value) {
                    return <p className="text-sm font-bold text-gray-900">-</p>;
                }

                return row.isMainCategory ? (
                    <div
                        className="flex items-center cursor-pointer"
                        onClick={() => toggleRowExpansion(row.Type)}
                    >
                        {row.subcategories && row.subcategories.length > 0 && (
                            expandedRows[row.Type] ? <IoChevronDown size={16} /> : <IoChevronForward size={16} />
                        )}
                        <p className="text-sm font-bold text-gray-900 ml-2">{value}</p>
                    </div>
                ) : row.isSubcategory ? (
                    <div className="flex items-center">
                        <div className="w-6 border-r border-gray-200 mr-2"></div>
                        <p className="text-sm text-gray-900 flex-1">{value}</p>
                    </div>
                ) : (
                    <p className="text-sm font-bold text-gray-900">{value}</p>
                );
            },
        });

        // Order periods according to their order in availableTimePeriods
        const orderedPeriods = [...selectedPeriods].sort((a, b) => {
            const indexA = availableTimePeriods.indexOf(a);
            const indexB = availableTimePeriods.indexOf(b);
            return indexA - indexB;
        });

        // Create a column group for each selected period
        const periodColumnGroups = orderedPeriods.map(period => {
            // Create a group column for this period
            return columnHelper.group({
                id: `${period}-group`,
                header: period ? period.toUpperCase() : '',
                isPeriodGroup: true, // Custom property to identify period groups
                columns: [
                    // Annual column for this period
                    columnHelper.accessor(row => {
                        // Get the data for this period from the facility's periods
                        const periodData = currentFacility?.periods?.[period]?.Categories?.Expenses || [];
                        const matchingItem = periodData.find(item => item.Type === row.Type);
                        return matchingItem?.Annual;
                    }, {
                        id: `${period}-annual`,
                        header: () => <p className="text-sm font-bold text-right text-gray-600 dark:text-white">ANNUAL</p>,
                        cell: (info) => (
                            <p className="text-right text-sm text-gray-900">
                                {formatCellValue(info.getValue())}
                            </p>
                        ),
                    }),
                    // PPD/Percentages column for this period
                    columnHelper.accessor(row => {
                        // Get the data for this period from the facility's periods
                        const periodData = currentFacility?.periods?.[period]?.Categories?.Expenses || [];
                        const matchingItem = periodData.find(item => item.Type === row.Type);
                        return {
                            value: matchingItem?.Percentages || matchingItem?.PPD,
                            isPPD: !matchingItem?.Percentages && matchingItem?.PPD
                        };
                    }, {
                        id: `${period}-ppd-percentages`,
                        header: () => <p className="text-sm font-bold text-right text-gray-600 dark:text-white">PPD/PERCENTAGES</p>,
                        cell: (info) => {
                            const { value, isPPD } = info.getValue();
                            const row = info.row.original;

                            if (!value) return <p className="text-right text-sm text-gray-900">-</p>;

                            // If it's a string that's not a number, display it as-is
                            if (typeof value === 'string' && isNaN(parseFloat(value))) {
                                return (
                                    <p className={`text-right text-sm ${isPPD ? 'text-red-900' : 'text-gray-900'}`}>
                                        {value}
                                    </p>
                                );
                            }

                            // Convert to string if it's a number
                            const strValue = typeof value === 'number' ? value.toString() : value;

                            // Only try to replace if it's a string
                            const numValue = typeof strValue === 'string' ?
                                parseFloat(strValue.replace('%', '')) :
                                parseFloat(strValue);

                            // If it's a PPD value, format as currency
                            if (isPPD) {
                                return (
                                    <p className="text-right text-sm text-red-900">
                                        {formatCellValue(numValue, true)}
                                    </p>
                                );
                            }

                            // Otherwise format as percentage
                            return (
                                <p className="text-right text-sm text-gray-900">
                                    {!isNaN(numValue) ? `${numValue.toFixed(1)}%` : value}
                                </p>
                            );
                        },
                    }),
                ],
            });
        });

        return [typeColumn, ...periodColumnGroups];
    }, [selectedPeriods, expandedRows, availableTimePeriods, currentFacility]);

    // Prepare rows including expanded subcategories
    const preparedRows = useMemo(() => {
        const rows = [];

        tableData.forEach(row => {
            rows.push(row);

            // If it's a main category and expanded, add its subcategories
            if (row.isMainCategory && expandedRows[row.Type] && row.subcategories) {
                rows.push(...row.subcategories.map(subcat => ({
                    ...subcat,
                    isSubcategory: true,
                    parentCategory: row.Type
                })));
            }
        });

        return rows;
    }, [tableData, expandedRows]);

    const table = useReactTable({
        data: preparedRows,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const handleTogglePeriod = (periods) => {
        setSelectedPeriods(prev => {
            // If we're setting an entirely new array of periods
            if (periods.length > 1) {
                return periods;
            }

            const period = periods[0];
            // Toggle individual period
            if (prev.includes(period)) {
                // Don't allow removing the current period
                if (period === currentPeriod && prev.length === 1) {
                    return prev;
                }
                return prev.filter(p => p !== period);
            } else {
                return [...prev, period];
            }
        });
    };

    const handleResetPeriods = () => {
        setSelectedPeriods(
            availableTimePeriods.length > 0 ? availableTimePeriods : [currentPeriod]
        );
    };

    const handleExport = () => {
        const dealName = entityName || (viewMode === 'portfolio' ? 'portfolio' : 'deal');
        const facilityName = currentFacility?.details?.Facilities || 'facility';
        const periodsText = selectedPeriods.length > 1 ? 'multiple_periods' : selectedPeriods[0];
        const filename = `${dealName}_${facilityName}_expenses_${periodsText}.csv`;

        exportFinanceTableToCSV(
            preparedRows,
            columns,
            currentFacility,
            selectedPeriods,
            filename
        );
    };

    const timePeriodSelector = (
        <TimePeriodSelector
            availablePeriods={availableTimePeriods}
            selectedPeriods={selectedPeriods}
            onTogglePeriod={handleTogglePeriod}
            onResetPeriods={handleResetPeriods}
        />
    );

    return (
        <TableContainer
            title="Expenses"
            timePeriodSelector={timePeriodSelector}
            onExport={handleExport}
        >
            <TableHeader headerGroups={table.getHeaderGroups()} />
            <tbody>
                {table.getRowModel().rows.map((row, index) => (
                    <tr
                        key={row.id}
                        className={`
                            border-t
                            ${row.original.Type.toLowerCase().includes('adjusted')
                                ? 'bg-gray-100 font-semibold border-t-2 border-gray-400'
                                : row.original.Type.toLowerCase().includes('total')
                                    ? 'bg-gray-50 font-medium border-t border-gray-300'
                                    : row.original.isSubcategory
                                        ? 'bg-gray-50 hover:bg-gray-100'
                                        : index % 2 === 0
                                            ? 'bg-white'
                                            : 'bg-blue-50'
                            }
                        `}
                    >
                        {row.getVisibleCells().map((cell, cellIndex) => {
                            // Check if this is the last cell in the row
                            const isLastColumn = cellIndex === row.getVisibleCells().length - 1;
                            const isTypeColumn = cell.column.id === 'type';

                            // Determine if this cell is the last in a period group
                            const isLastInGroup = isLastInPeriodGroup(cell, row, isLastColumn);
                            const borderClass = isLastInGroup ? 'border-r-2 border-gray-300' : '';

                            // Enhanced styling for frozen column
                            const frozenColumnStyles = isTypeColumn ? {
                                position: 'sticky',
                                left: 0,
                                zIndex: 20,
                                backgroundColor: 'inherit',
                                boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                            } : {};

                            return (
                                <td
                                    key={cell.id}
                                    className={`px-3 py-2 ${isTypeColumn
                                        ? 'text-left min-w-[200px]'
                                        : 'text-right min-w-[120px]'
                                        } ${borderClass}`}
                                    style={frozenColumnStyles}
                                >
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                    )}
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </TableContainer>
    );
}

function EbitdarTable({ data, availableTimePeriods = [], currentPeriod }) {
    const { viewMode } = useViewMode();
    const { currentFacility: dealFacility, currentDealName } = useDealData();
    const { currentPortfolioFacility, currentPortfolioName } = usePortfolioData();
    const currentFacility = viewMode === 'portfolio' ? currentPortfolioFacility : dealFacility;
    const entityName = viewMode === 'portfolio' ? currentPortfolioName : currentDealName;
    const [tableData, setTableData] = useState([]);
    const [selectedPeriods, setSelectedPeriods] = useState(
        availableTimePeriods.length > 0 ? availableTimePeriods : [currentPeriod]
    );

    useEffect(() => {
        if (
            availableTimePeriods.length > 0 &&
            (selectedPeriods.length !== availableTimePeriods.length ||
                !selectedPeriods.every(p => availableTimePeriods.includes(p)))
        ) {
            setSelectedPeriods(availableTimePeriods);
        } else if (currentPeriod && selectedPeriods.length === 0) {
            setSelectedPeriods([currentPeriod]);
        }
    }, [currentPeriod, availableTimePeriods]);

    useEffect(() => {
        // Filter to show only EBITDA/EBITDAR items
        const filteredData = data.filter(item =>
            item && item.Type && (
                (item.Type.toUpperCase().includes('EBITDA') ||
                    item.Type.toUpperCase().includes('EBITDAR')) &&
                !item.Type.includes('Check')
            )
        );
        setTableData(filteredData);
    }, [data]);

    // Create columns based on selected time periods with grouping
    const columns = useMemo(() => {
        const typeColumn = columnHelper.accessor("Type", {
            id: "type",
            header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">TYPE</p>,
            cell: (info) => {
                const value = info.getValue();
                return (
                    <p className="text-sm text-gray-900 font-bold">{value || '-'}</p>
                );
            },
        });

        // Order periods according to their order in availableTimePeriods
        const orderedPeriods = [...selectedPeriods].sort((a, b) => {
            const indexA = availableTimePeriods.indexOf(a);
            const indexB = availableTimePeriods.indexOf(b);
            return indexA - indexB;
        });

        // Create a column group for each selected period
        const periodColumnGroups = orderedPeriods.map(period => {
            // Create a group column for this period
            return columnHelper.group({
                id: `${period}-group`,
                header: period ? period.toUpperCase() : '',
                isPeriodGroup: true, // Custom property to identify period groups
                columns: [
                    // Annual column for this period
                    columnHelper.accessor(row => {
                        // Get the data for this period from the facility's periods
                        const periodData = currentFacility?.periods?.[period]?.Categories?.Expenses || [];
                        const matchingItem = periodData.find(item => item.Type === row.Type);
                        return matchingItem?.Annual;
                    }, {
                        id: `${period}-annual`,
                        header: () => <p className="text-sm font-bold text-right text-gray-600 dark:text-white">ANNUAL</p>,
                        cell: (info) => (
                            <p className="text-right text-sm text-gray-900">
                                {formatCellValue(info.getValue())}
                            </p>
                        ),
                    }),
                    // Margin column for this period (using PPD field)
                    columnHelper.accessor(row => {
                        // Get the data for this period from the facility's periods
                        const periodData = currentFacility?.periods?.[period]?.Categories?.Expenses || [];
                        const matchingItem = periodData.find(item => item.Type === row.Type);
                        return matchingItem?.PPD;
                    }, {
                        id: `${period}-margin`,
                        header: () => <p className="text-sm font-bold text-right text-gray-600 dark:text-white">MARGIN</p>,
                        cell: (info) => {
                            const value = info.getValue();
                            if (value === null || value === undefined || value === '') {
                                return <p className="text-right text-sm text-gray-900">-</p>;
                            }

                            // Convert PPD value to percentage
                            const numValue = typeof value === 'number' ? value : parseFloat(value);

                            if (isNaN(numValue)) {
                                return <p className="text-right text-sm text-gray-900">-</p>;
                            }

                            // Convert to percentage (multiply by 100)
                            const percentage = (numValue * 100).toFixed(0);

                            return (
                                <p className="text-right text-sm text-gray-900">
                                    {percentage}%
                                </p>
                            );
                        },
                    }),
                ],
            });
        });

        return [typeColumn, ...periodColumnGroups];
    }, [selectedPeriods, availableTimePeriods, currentFacility]);

    const [columnFilters, setColumnFilters] = useState([]);
    const [globalFilter, setGlobalFilter] = useState("");

    const table = useReactTable({
        data: tableData,
        columns,
        state: {
            columnFilters,
            globalFilter,
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const handleTogglePeriod = (periods) => {
        setSelectedPeriods(prev => {
            // If we're setting an entirely new array of periods
            if (periods.length > 1) {
                return periods;
            }

            const period = periods[0];
            // Toggle individual period
            if (prev.includes(period)) {
                // Don't allow removing the current period
                if (period === currentPeriod && prev.length === 1) {
                    return prev;
                }
                return prev.filter(p => p !== period);
            } else {
                return [...prev, period];
            }
        });
    };

    const handleResetPeriods = () => {
        setSelectedPeriods(
            availableTimePeriods.length > 0 ? availableTimePeriods : [currentPeriod]
        );
    };

    const handleExport = () => {
        const dealName = entityName || (viewMode === 'portfolio' ? 'portfolio' : 'deal');
        const facilityName = currentFacility?.details?.Facilities || 'facility';
        const periodsText = selectedPeriods.length > 1 ? 'multiple_periods' : selectedPeriods[0];
        const filename = `${dealName}_${facilityName}_ebitdar_${periodsText}.csv`;

        exportFinanceTableToCSV(
            tableData,
            columns,
            currentFacility,
            selectedPeriods,
            filename
        );
    };

    const timePeriodSelector = (
        <TimePeriodSelector
            availablePeriods={availableTimePeriods}
            selectedPeriods={selectedPeriods}
            onTogglePeriod={handleTogglePeriod}
            onResetPeriods={handleResetPeriods}
        />
    );

    return (
        <TableContainer
            title="EBITDAR"
            timePeriodSelector={timePeriodSelector}
            onExport={handleExport}
        >
            <TableHeader headerGroups={table.getHeaderGroups()} />
            <tbody>
                {table.getRowModel().rows.map((row, index) => (
                    <tr
                        key={row.id}
                        className={`
                            border-t
                            ${row.original.Type.toLowerCase().includes('adjusted')
                                ? 'bg-gray-100 font-semibold border-t-2 border-gray-400'
                                : row.original.Type.toLowerCase().includes('total')
                                    ? 'bg-gray-50 font-medium border-t border-gray-300'
                                    : index % 2 === 0 ? 'bg-white' : 'bg-blue-50'
                            }
                        `}
                    >
                        {row.getVisibleCells().map((cell, cellIndex) => {
                            // Check if this is the last cell in the row
                            const isLastColumn = cellIndex === row.getVisibleCells().length - 1;
                            const isTypeColumn = cell.column.id === 'type';

                            // Determine if this cell is the last in a period group
                            const isLastInGroup = isLastInPeriodGroup(cell, row, isLastColumn);
                            const borderClass = isLastInGroup ? 'border-r-2 border-gray-300' : '';

                            // Enhanced styling for frozen column
                            const frozenColumnStyles = isTypeColumn ? {
                                position: 'sticky',
                                left: 0,
                                zIndex: 20,
                                backgroundColor: 'inherit',
                                boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                            } : {};

                            return (
                                <td
                                    key={cell.id}
                                    className={`px-3 py-2 ${isTypeColumn
                                        ? 'text-left min-w-[200px]'
                                        : 'text-right min-w-[120px]'
                                        } ${borderClass}`}
                                    style={frozenColumnStyles}
                                >
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                    )}
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </TableContainer>
    );
}

export { RevenueTable, ExpenseTable, EbitdarTable };